import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import {
  PlayCircle,
  Landmark,
  Compass,
  FileText,
  LineChart,
  ArrowRight,
  Quote,
  Plus
} from 'lucide-react';

export default function Landing() {
  const audienceGroups = [
    {
      title: 'For agents',
      description:
        'Use indicative feasibility views to structure listing and developer conversations, while making it clear that the calculator is a preliminary tool and not a valuation or guarantee of performance.',
    },
    {
      title: 'For vendors',
      description:
        'See an initial snapshot of how a site might look to developers, as a starting point only. You should always obtain independent professional advice before making decisions.',
    },
    {
      title: 'For developers',
      description:
        'Screen and track multiple sites with consistent assumptions. Results are indicative only and must be verified through your own analysis, due diligence and advice.',
    },
  ];

  const firstPassSteps = [
    {
      number: '01',
      title: 'Enter your assumptions',
      description: 'You enter basic site and project assumptions.',
    },
    {
      number: '02',
      title: 'We generate indicative scenarios',
      description:
        'We combine your inputs with available data sources to generate indicative scenarios.',
    },
    {
      number: '03',
      title: 'Use it as a first-pass sense-check',
      description:
        'You use the output as a first-pass sense-check before doing deeper due diligence with your advisers.',
    },
  ];

  return (
    <div className="min-h-screen selection:bg-primary-container selection:text-on-primary-container">
      <a className="sr-only focus:not-sr-only fixed top-4 left-4 z-[100] bg-primary text-on-primary px-4 py-2" href="#main-content">Skip to content</a>

      {/* Top Navigation Shell */}
      <nav className="fixed top-2 left-1/2 -translate-x-1/2 w-[95%] rounded-lg z-50 bg-white/80 dark:bg-emerald-950/80 backdrop-blur-md flex justify-between items-center px-8 py-4 max-w-7xl mx-auto shadow-[0_4px_20px_oklch(0_0_0_/_0.04)]">
        <Link to="/" className="text-primary hover:opacity-80 transition-opacity">
          <Logo className="h-10" />
        </Link>
        <div className="flex items-center gap-4">
          <Link className="bg-primary text-on-primary px-5 py-2 text-sm font-bold uppercase tracking-widest rounded-sm scale-95 active:scale-90 transition-transform" to="/auth">Get Started</Link>
        </div>
      </nav>

      <main id="main-content">
        {/* Hero Section */}
        <section className="pt-40 pb-20 px-6 max-w-7xl mx-auto">
          <div className="editorial-grid">
            <div className="col-span-12 lg:col-span-8">
              <h1 className="text-[clamp(2.5rem,6vw,5rem)] font-headline font-semibold leading-[1.1] tracking-tight text-primary mb-8">
                Early-Stage Feasibility, <span className="text-on-primary-container">Made Simple.</span>
              </h1>
              <p className="text-lg md:text-xl text-on-surface-variant max-w-2xl font-body leading-relaxed mb-10">
                An online tool to help agents, vendors and developers run first-pass feasibility checks - a streamlined starting point for your next project.
              </p>
              <div className="flex flex-wrap gap-4 mb-16">
                <Link className="bg-primary text-on-primary px-8 py-4 font-bold uppercase tracking-widest rounded-sm hover:bg-primary-container transition-colors" to="/auth">
                  Try an Indicative Feasibility Check
                </Link>
                <a className="ghost-border px-8 py-4 font-bold uppercase tracking-widest rounded-sm hover:bg-surface-container-high transition-colors flex items-center gap-2" href="#how-it-works">
                  <PlayCircle size={20} /> Learn How It Works
                </a>
              </div>
              <p className="text-sm text-on-surface-variant/70 font-body mb-10">
                Indicative information only. Not financial, legal, tax or other professional advice.
              </p>
              <div className="flex items-center gap-6 opacity-60 grayscale">
                <span className="text-xs font-bold uppercase tracking-widest">Trusted by leading firms</span>
                <div className="h-[1px] w-12 bg-outline-variant"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Social Proof Bar */}
        <section className="bg-surface-container-low py-12 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col">
              <span className="text-primary text-4xl font-headline font-bold">Over $500M</span>
              <span className="text-on-surface-variant uppercase text-xs font-bold tracking-tighter">Site value analysed to date</span>
            </div>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-40">
              <div className="flex items-center gap-2">
                <svg viewBox="0 0 100 100" className="w-6 h-6 text-primary fill-current">
                  <path d="M 0 100 L 0 0 A 100 100 0 0 1 100 100 Z" />
                </svg>
                <span className="text-xl font-headline font-bold tracking-widest">BARRY PLANT</span>
              </div>
              <span className="text-xl font-headline font-bold tracking-widest">RAY WHITE</span>
              <span className="text-xl font-headline font-bold tracking-widest">JELLIS CRAIG</span>
              <span className="text-xl font-headline font-bold tracking-widest">FLETCHERS</span>
              <span className="text-xl font-headline font-bold tracking-widest">BUXTON</span>
            </div>
          </div>
        </section>

        {/* Audience Fit */}
        <section className="clamp-padding px-6 max-w-7xl mx-auto">
          <div className="editorial-grid">
            <div className="col-span-12 mb-12">
              <span className="text-on-primary-container font-bold uppercase tracking-widest text-xs mb-4 block">Who it's for</span>
              <h2 className="text-4xl md:text-5xl font-headline font-medium text-primary">
                Built for agents, vendors and developers.
              </h2>
            </div>
            {audienceGroups.map((group) => (
              <div key={group.title} className="col-span-12 md:col-span-4 bg-surface-container-low rounded-xl p-8">
                <h3 className="text-2xl font-headline font-bold text-primary mb-4">{group.title}</h3>
                <p className="text-on-surface-variant font-body leading-relaxed">{group.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Key Services: Bento Grid */}
        <section className="clamp-padding bg-surface-container-low px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-16">
              <h2 className="text-3xl font-headline font-bold text-primary">The Precision Suite</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              {/* Scout */}
              <div className="md:col-span-8 bg-surface p-10 rounded-xl flex flex-col justify-between min-h-[400px] shadow-sm hover:shadow-xl transition-all group">
                <div>
                  <Compass className="text-on-primary-container mb-6" size={40} strokeWidth={1.5} />
                  <h3 className="text-2xl font-headline font-bold mb-4">Scout</h3>
                  <p className="text-on-surface-variant max-w-md leading-relaxed">Advanced site finding with granular filters. Search for specific lot sizes, frontage requirements, and zoning categories across the entire state instantly.</p>
                </div>
                <div className="mt-8 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-widest text-primary">Explore Tool</span>
                  <ArrowRight className="opacity-0 group-hover:opacity-100 transition-opacity text-primary" size={20} />
                </div>
              </div>

              {/* Feasibility */}
              <div className="md:col-span-4 bg-primary text-on-primary p-10 rounded-xl flex flex-col justify-between shadow-sm hover:scale-[1.02] transition-transform">
                <div>
                  <Landmark className="mb-6 text-on-primary" size={40} strokeWidth={1.5} />
                  <h3 className="text-2xl font-headline font-bold mb-4">Feasibility & Site Fit</h3>
                  <p className="opacity-80 leading-relaxed">Comprehensive financial modelling with automated GIS footprint previews. Instantly calculate indicative margins, ROE, and equity requirements while factoring in contour fall, planning zones, and local comparable sales.</p>
                </div>
              </div>

              {/* Reports */}
              <div className="md:col-span-4 bg-surface-container-highest p-10 rounded-xl flex flex-col justify-between shadow-sm">
                <div>
                  <FileText className="text-primary mb-6" size={40} strokeWidth={1.5} />
                  <h3 className="text-2xl font-headline font-bold mb-4">Reports</h3>
                  <p className="text-on-surface-variant leading-relaxed">Professional documentation for investors and banks. Generate white-labeled PDFs with one click.</p>
                </div>
              </div>

              {/* Data Integration */}
              <div className="md:col-span-8 bg-white p-10 rounded-xl border border-outline-variant/10 shadow-sm flex items-center gap-12">
                <div className="flex-1">
                  <h3 className="text-2xl font-headline font-bold mb-4">Live GIS Data</h3>
                  <p className="text-on-surface-variant">Real-time integration with state spatial records and local council LEP datasets ensure your data is never stale.</p>
                </div>
                <div className="hidden sm:flex w-32 h-32 bg-surface-container-low rounded-full items-center justify-center">
                  <LineChart className="text-on-primary-container" size={48} strokeWidth={1.5} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="clamp-padding px-6 max-w-7xl mx-auto" id="how-it-works">
          <div className="editorial-grid">
            <div className="col-span-12 mb-16">
              <h2 className="text-4xl font-headline font-bold text-primary mb-6">How it works</h2>
              <p className="text-lg text-on-surface-variant font-body leading-relaxed max-w-3xl">
                The tool is designed to help you structure your thinking and conversations - it is not intended to be relied on as a valuation, feasibility sign-off or investment recommendation.
              </p>
            </div>
            {firstPassSteps.map((step) => (
              <div key={step.number} className="col-span-12 md:col-span-4 relative mb-12 md:mb-0 last:mb-0">
                <div className="text-[8rem] font-headline font-bold text-on-primary-container opacity-10 absolute -top-16 -left-4 leading-none">
                  {step.number}
                </div>
                <div className="pt-8 relative z-10">
                  <h4 className="text-xl font-headline font-bold mb-4">{step.title}</h4>
                  <p className="text-on-surface-variant leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust Block */}
        <section className="clamp-padding bg-primary text-on-primary px-6">
          <div className="max-w-7xl mx-auto editorial-grid items-center">
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-primary-container p-12 rounded-lg border border-on-primary-container/20">
                <Quote className="mb-6 text-on-primary-container" size={40} strokeWidth={1.5} />
                <p className="text-2xl font-headline leading-relaxed mb-8 italic">
                  "The feasibility calculator has completely transformed my off-market prospecting. By presenting homeowners with instant, data-backed development potential, I secured three new listings in a single afternoon."
                </p>
                <a 
                  href="https://www.barryplant.com.au/offices/manningham/our-team/joseph-tilley/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex items-center gap-4 group hover:opacity-90 transition-opacity"
                >
                  <div className="w-12 h-12 rounded-full bg-surface-variant overflow-hidden ring-2 ring-transparent group-hover:ring-on-primary-container/30 transition-all">
                    <img alt="Joseph Tilley Profile" className="object-cover w-full h-full object-top" src="https://images2.barryplant.com.au/media/images/Joe_022_Blog.aa01da47.fill-540x540.jpg" referrerPolicy="no-referrer" />
                  </div>
                  <div>
                    <p className="font-bold group-hover:underline decoration-on-primary-container/50 underline-offset-4">Joseph Tilley</p>
                    <p className="text-sm opacity-60">Licensed Estate Agent, Barry Plant Manningham</p>
                  </div>
                </a>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex flex-col gap-12 mt-12 lg:mt-0">
              <div>
                <p className="text-5xl font-headline font-bold text-on-primary-container">32%</p>
                <p className="text-lg opacity-80 mt-2">Increase in acquisition efficiency across multi-site portfolios.</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="clamp-padding px-6 max-w-3xl mx-auto">
          <h2 className="text-3xl font-headline font-bold text-primary mb-12">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: "How accurate is the zoning data?",
                a: "Our data is sourced directly from state government GIS repositories and updated weekly. We integrate LEP and DCP overlays to provide the most current zoning information available on the market."
              },
              {
                q: "Can I export reports for my financier?",
                a: "Yes, Site Yield generates professional PDF reports formatted for institutional lending requirements, including full project budgets and sensitivity analyses."
              },
              {
                q: "Does Site Yield cover regional areas?",
                a: "We currently offer comprehensive coverage across all major metropolitan and high-growth regional corridors in Australia, including NSW, VIC, and QLD."
              },
              {
                q: "Can I customise the feasibility variables?",
                a: "Absolutely. You can override any default cost assumptions, including interest rates, construction costs per m2, and sales commissions to match your specific project realities."
              }
            ].map((faq, i) => (
              <details key={i} className="group bg-surface-container-low rounded-lg p-6 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex justify-between items-center cursor-pointer font-bold text-primary list-none">
                  {faq.q}
                  <Plus className="group-open:rotate-45 transition-transform" size={20} />
                </summary>
                <p className="mt-4 text-on-surface-variant leading-relaxed">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="clamp-padding px-6">
          <div className="max-w-7xl mx-auto bg-primary-container rounded-2xl p-12 md:p-20 text-center flex flex-col items-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <img alt="Abstract architectural building" className="object-cover w-full h-full" src="https://lh3.googleusercontent.com/aida-public/AB6AXuD8viFNbpdmqRzNJd9cPBAoFCdJN7UC7dn7SJp30WomF6EwHT-nm-Kzz5GyE-Ui4Sly00AhUUiw7qlZntZx57bq5WJA78S5i4uodqH_xcE73eF09rIjI9MeeSKNi1JkJNd_JifAimsRHJgDGDRNpFGeL-PkSqo5fsE7WteexPrKLR_qDnOLaL9_7UmHVPQQicYSo2IVENkl5zpW0JHSBmlLwG2qlXwisIPfDkgF3MYBCfqpawatsrzjV81iHFnnbK_oWSnd312NyeiP" />
            </div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-headline font-bold text-on-primary mb-8 max-w-3xl mx-auto">Ready to secure your next high-yield development?</h2>
              <p className="text-on-primary/80 text-lg md:text-xl max-w-2xl mx-auto mb-12">
                Join hundreds of developers who have optimised their site finding workflow with Site Yield.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link className="bg-primary text-on-primary px-10 py-5 font-bold uppercase tracking-widest text-sm rounded-sm hover:scale-105 transition-transform" to="/auth">Start Free Trial</Link>
                <a className="bg-transparent border border-on-primary/20 text-on-primary px-10 py-5 font-bold uppercase tracking-widest text-sm rounded-sm hover:bg-on-primary/5 transition-colors" href="#">Speak to Sales</a>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-surface-container-low w-full border-t border-outline-variant/20 font-body text-base">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 px-8 py-16 max-w-7xl mx-auto">
          <div className="md:col-span-3">
            <Logo className="h-10 text-primary mb-6" />
            <p className="text-on-surface-variant max-w-xs mb-8">
              Precision software for the next generation of property developers.
            </p>
          </div>
          <div>
            <h5 className="text-primary font-bold mb-6 uppercase text-xs tracking-widest">Legal</h5>
            <ul className="space-y-4">
              <li><a className="text-on-surface-variant hover:text-primary transition-colors" href="#">Privacy Policy</a></li>
              <li><a className="text-on-surface-variant hover:text-primary transition-colors" href="#">Terms of Service</a></li>
              <li><a className="text-on-surface-variant hover:text-primary transition-colors" href="#">Cookie Notice</a></li>
              <li><a className="text-on-surface-variant hover:text-primary transition-colors" href="#">Output Disclaimer</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-8 py-8 border-t border-outline-variant/20">
          <p className="text-xs text-on-surface-variant text-center mb-3">
            The information and tools on this site are general in nature and for information purposes only. They do not take into account your objectives, financial situation or needs and are not a substitute for professional advice. Outputs are based on user inputs and available data at the time and are indicative only.
          </p>
          <p className="text-xs text-on-surface-variant text-center">© 2026 Site Yield Pty Ltd. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
